import { useInView, useReducedMotion, useSpring, type MotionValue, type SpringOptions } from "motion/react";
import { useEffect, type RefObject } from "react";

interface LookAtCursorOptions {
  /** Point to look from, as fractions of the element's box. */
  origin: { x: number; y: number };
  /** Farthest offset, as a fraction of the element's width. */
  reach: number;
  /** Cursor distance (px) at which the offset reaches `reach`; closer moves less. */
  fullReachDistance: number;
  spring: SpringOptions;
}

/**
 * Offsets that shift children of an element toward the cursor, like pupils
 * glancing at it. Listens only while the element is on screen, and stays at
 * zero under reduced motion.
 *
 * @param ref - The element to look from. Must not move with the returned offsets.
 * @param options - Where to look from, how far, and how smoothly.
 * @returns Spring-smoothed `x` and `y` offsets in px.
 */
export const useLookAtCursor = (
  ref: RefObject<HTMLElement | null>,
  { origin, reach, fullReachDistance, spring }: LookAtCursorOptions
): { x: MotionValue<number>; y: MotionValue<number> } => {
  // Hooks
  const reduceMotion = useReducedMotion();
  const inView = useInView(ref);
  const x = useSpring(0, spring);
  const y = useSpring(0, spring);

  // Effects
  useEffect(() => {
    if (reduceMotion || !inView) return;

    const handlePointerMove = (event: PointerEvent) => {
      const el = ref.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const dx = event.clientX - (rect.left + rect.width * origin.x);
      const dy = event.clientY - (rect.top + rect.height * origin.y);
      const distance = Math.hypot(dx, dy);
      if (distance === 0) return;

      const offset = rect.width * reach * Math.min(1, distance / fullReachDistance);
      x.set((dx / distance) * offset);
      y.set((dy / distance) * offset);
    };

    window.addEventListener("pointermove", handlePointerMove, { passive: true });
    return () => window.removeEventListener("pointermove", handlePointerMove);
  }, [ref, reduceMotion, inView, origin, reach, fullReachDistance, x, y]);

  return { x, y };
};
